import type jsPDF from 'jspdf';
import {
  PDFQuoteData,
  buildPalette,
  loadLogo,
  getJsPdfImageFormat,
  resolveDocumentTitle,
  fitHeaderTitle,
  formatQuoteDate,
  getDataUrl,
  renderFooter,
  detectDocumentKind,
  formatDocument,
  renderLogoWatermark,
} from './shared';
import { maskDocument } from '@/lib/documentUtils';

/**
 * Template de CONTRATO (documentType === 'contrato').
 *
 * Diferente dos demais documentos, aqui NÃO há tabela de itens: o corpo é o
 * texto do contrato (multi-parágrafo). Ao final, imprime as DUAS assinaturas
 * (imagens PNG) — "Contratante (cliente)" e "Contratado (prestador)" — com
 * nome/documento/data sob cada uma, e um rodapé discreto de AUDITORIA (data/hora
 * de cada assinatura, CPF confirmado mascarado, IP do cliente e SHA-256 do
 * conteúdo assinado). É um renderer dedicado justamente para não tocar nos
 * templates dos outros documentos.
 */

const fmtDateTime = (iso?: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  } catch {
    return '—';
  }
};

export async function render(doc: jsPDF, data: PDFQuoteData): Promise<void> {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Marca-d'agua da logo: PRIMEIRA instrucao de desenho da pagina.
  // jsPDF pinta na ordem das chamadas e nao tem z-index, entao desenhada
  // depois ela cobriria a tabela e o total em vez de ficar atras deles.
  await renderLogoWatermark(doc, data);
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 16;
  const contentWidth = pageWidth - margin * 2;
  const bottomLimit = pageHeight - 16;
  const { primaryColor, grayVeryLight, grayText, darkText, white } = buildPalette(data.brandColorRGB);

  const logoBase64 = await loadLogo(data.company?.logoUrl);

  // === HEADER ===
  const headerHeight = 40;
  doc.setFillColor(...primaryColor);
  doc.rect(0, 0, pageWidth, headerHeight, 'F');

  doc.setTextColor(...white);
  doc.setFont('helvetica', 'bold');
  const tituloCabecalho = fitHeaderTitle(doc, resolveDocumentTitle(data), pageWidth - margin * 2, 22);
  doc.setFontSize(tituloCabecalho.size);
  doc.text(tituloCabecalho.text, margin, 18);

  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.text(data.company?.name || 'Minha Empresa', margin, 27);
  if (data.company?.cnpj) {
    doc.setFontSize(9);
    doc.text(`CNPJ/CPF: ${data.company.cnpj}`, margin, 33);
  }

  if (logoBase64) {
    try {
      const logoBoxSize = 24;
      doc.addImage(
        logoBase64,
        getJsPdfImageFormat(logoBase64),
        pageWidth - margin - logoBoxSize,
        8,
        logoBoxSize,
        logoBoxSize,
      );
    } catch {
      /* sem logo — segue sem placeholder no contrato */
    }
  }

  let yPos = headerHeight + 10;

  // === PARTES ===
  const clientDocLabel = data.client.document
    ? `${detectDocumentKind(data.client.document) === 'cnpj' ? 'CNPJ' : 'CPF'}: ${formatDocument(data.client.document)}`
    : '';
  const partyBlockHeight = 26;
  doc.setFillColor(...grayVeryLight);
  doc.setDrawColor(...primaryColor);
  doc.setLineWidth(0.4);
  doc.roundedRect(margin, yPos, contentWidth, partyBlockHeight, 2, 2, 'FD');

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...grayText);
  doc.text('CONTRATANTE (cliente)', margin + 4, yPos + 6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkText);
  doc.text(doc.splitTextToSize(data.client.name || 'Não informado', contentWidth / 2 - 8)[0], margin + 4, yPos + 12);
  if (clientDocLabel) {
    doc.setFontSize(7);
    doc.setTextColor(...grayText);
    doc.text(clientDocLabel, margin + 4, yPos + 17);
  }

  const rightX = margin + contentWidth / 2 + 2;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...grayText);
  doc.text('CONTRATADO (prestador)', rightX, yPos + 6);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkText);
  doc.text(doc.splitTextToSize(data.company?.name || 'Minha Empresa', contentWidth / 2 - 8)[0], rightX, yPos + 12);
  doc.setFontSize(7);
  doc.setTextColor(...grayText);
  doc.text(`Emissão: ${formatQuoteDate(data.quoteDate)}`, rightX, yPos + 17);

  yPos += partyBlockHeight + 8;

  // === CORPO DO CONTRATO ===
  doc.setFontSize(9.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkText);

  const paragraphs = String(data.contractContent || '').split('\n');
  const lineHeight = 5;
  for (const paragraph of paragraphs) {
    if (paragraph.trim() === '') {
      yPos += lineHeight * 0.6;
      continue;
    }
    const lines = doc.splitTextToSize(paragraph, contentWidth);
    for (const line of lines) {
      if (yPos > bottomLimit) {
        doc.addPage();
        yPos = 20;
      }
      doc.text(line, margin, yPos);
      yPos += lineHeight;
    }
    yPos += lineHeight * 0.4;
  }

  // === ASSINATURAS ===
  // Bloco com as duas assinaturas lado a lado. Se não couber, nova página.
  const sigBlockHeight = 54;
  if (yPos > bottomLimit - sigBlockHeight) {
    doc.addPage();
    yPos = 20;
  } else {
    yPos += 6;
  }

  const colGap = 10;
  const colWidth = (contentWidth - colGap) / 2;
  const sigImgHeight = 22;
  const sigTop = yPos;

  const renderSignatureColumn = async (
    x: number,
    label: string,
    signatureDataUrl: string | null | undefined,
    signerName: string | null | undefined,
    signerDoc: string | null | undefined,
    signedAt: string | null | undefined,
  ) => {
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...grayText);
    doc.text(label, x, sigTop);

    // Imagem da assinatura (se houver).
    const imgTop = sigTop + 4;
    if (signatureDataUrl) {
      try {
        const img = await getDataUrl(signatureDataUrl);
        if (img) {
          doc.addImage(img, getJsPdfImageFormat(img), x, imgTop, colWidth, sigImgHeight);
        }
      } catch {
        /* segue sem a imagem — a linha e os dados abaixo ainda comprovam */
      }
    }

    const lineY = imgTop + sigImgHeight + 2;
    doc.setDrawColor(...grayText);
    doc.setLineWidth(0.3);
    doc.line(x, lineY, x + colWidth, lineY);

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...darkText);
    doc.text(doc.splitTextToSize(signerName?.trim() || '—', colWidth)[0], x, lineY + 5);

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...grayText);
    let metaY = lineY + 9;
    if (signerDoc?.trim()) {
      doc.text(`Doc.: ${maskDocument(signerDoc)}`, x, metaY);
      metaY += 4;
    }
    doc.text(signedAt ? `Assinado em ${fmtDateTime(signedAt)}` : 'Aguardando assinatura', x, metaY);
  };

  await renderSignatureColumn(
    margin,
    'Contratante (cliente)',
    data.clientSignatureDataUrl,
    data.clientSignerName,
    data.clientSignerDocument,
    data.clientSignedAt,
  );
  await renderSignatureColumn(
    margin + colWidth + colGap,
    'Contratado (prestador)',
    data.providerSignatureDataUrl,
    data.providerSignerName,
    null,
    data.providerSignedAt,
  );

  yPos = sigTop + sigBlockHeight;

  // === RODAPÉ DE AUDITORIA ===
  const auditLines: string[] = [];
  auditLines.push(
    'Assinatura eletrônica com trilha de evidências (MP 2.200-2 / Lei 14.063/2020). Não é assinatura ICP-Brasil.',
  );
  if (data.clientSignedAt) {
    const docPart = data.clientSignerDocument ? ` · Doc. confirmado: ${maskDocument(data.clientSignerDocument)}` : '';
    const ipPart = data.auditIp ? ` · IP: ${data.auditIp}` : '';
    auditLines.push(`Cliente assinou em ${fmtDateTime(data.clientSignedAt)}${docPart}${ipPart}.`);
  }
  if (data.providerSignedAt) {
    auditLines.push(`Prestador assinou em ${fmtDateTime(data.providerSignedAt)}.`);
  }
  if (data.auditContentSha256) {
    auditLines.push(`Hash do conteúdo assinado (SHA-256): ${data.auditContentSha256}`);
  }

  const auditText = auditLines.flatMap((l) => doc.splitTextToSize(l, contentWidth - 8) as string[]);
  const auditBoxHeight = auditText.length * 3.6 + 8;
  if (yPos > bottomLimit - auditBoxHeight) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFillColor(245, 245, 245);
  doc.roundedRect(margin, yPos, contentWidth, auditBoxHeight, 2, 2, 'F');
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...grayText);
  doc.text('Trilha de auditoria', margin + 4, yPos + 5);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  let auditY = yPos + 9;
  for (const line of auditText) {
    doc.text(line, margin + 4, auditY);
    auditY += 3.6;
  }

  // === FOOTER (branding + sequência) ===
  renderFooter(doc, data, { primaryColor, grayText, margin, pageWidth, pageHeight });
}
